import React, { useEffect, useState, useContext } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/time.css";
import { getDayWiseTracking, getUser } from "../services/adminServices";
import { AppContext } from "../context/AppContext";
import { useMemo } from "react";
import { useRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import SearchableDropdown from "./SearchableDropdown ";

const colorPalette = [
  "#4285f4", "#9c27b0", "#ff9800", "#1a73e8", "#e91e63",
  "#009688", "#f44336", "#7b1fa2", "#4caf50", "#ff5722",
  "#607d8b", "#9e9e9e", "#795548", "#2196f3", "#673ab7",
  "#ffc107", "#03a9f4", "#e91e63", "#4caf50", "#ff9800",
  "#00bcd4", "#8bc34a", "#cddc39", "#ffeb3b", "#ff5722",
  "#795548", "#607d8b", "#3f51b5", "#009688", "#e91e63",
  "#f06292", "#ba68c8", "#4db6ac", "#81c784", "#aed581"
];

const totalHours = 24;

export const DayWiseTimeTracker = () => {
  const { login } = useContext(AppContext);
  const [currentDate, setCurrentDate] = useState(moment(new Date()));
  const [timeData, setTimeData] = useState({
    startTime: "00:00",
    endTime: "00:00",
    duration: "0h 0min 0sec",
  });
  const [activityDetails, setActivityDetails] = useState([]);
  const [tooltip, setTooltip] = useState({
    "visible": false,
    "content": "",
    "x": 0,
    "y": 0
  });
  const [syncedWindows, setSyncedWindows] = useState([]);
  const [pagination, setPagination] = useState({});
  const [duration, setDuration] = useState({})
  const [filter, setFilter] = useState({ page: 1, limit: 50, key: 'time', direction: 'desc', userId: login.user._id, endTime: new Date(), startTime: new Date(), title: "", ownerName: "" });
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([])
  const [loading, setLoading] = useState(false)
  const [fullData, setFullData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [conformationModal, setConformationModal] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(login.user._id)
  const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const handleSort = (key) => {
    setFilter((prev) => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, key, direction };
    });
  };

  const handlePrev = () => {
    if (filter.page > 1) {
      setFilter({ ...filter, page: filter.page - 1 });
    }
  };

  const handleNext = () => {
    const totalPages = Math.ceil(fullData.length / filter.limit);
    if (filter.page < totalPages) {
      setFilter({ ...filter, page: filter.page + 1 });
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setFilter({ ...filter, limit: newLimit, page: 1 }); // reset to page 1
  };

  const totalPages = Math.ceil(fullData.length / filter.limit);

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
    );
  };

  const getAllUsers = async () => {
    try {
      let result = await getUser(login.token, { name: "" });
      setUsers(result.data);
      setSelectedUser(result.data[0]._id)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const ownerColorMap = useMemo(() => {
    const map = {};
    let colorIndex = 0;
    activityDetails.forEach((activity) => {
      const ownerName = activity.ownerName || "Unknown";
      if (!map[ownerName]) {
        map[ownerName] = colorPalette[colorIndex % colorPalette.length];
        colorIndex++;
      }
    });
    return map;
  }, [activityDetails]);

  const formatTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  const formatAppWiseDuration = (hours) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h === 0 ? `${m}min` : m === 0 ? `${h}h` : `${h}h ${m}min`;
  };

  const convertToActivityData = (data) => {
    if (!data || data.length === 0) {
      setActivityDetails([]);
      setTimeData({
        startTime: "00:00",
        endTime: "00:00",
        duration: "0h 0min 0sec",
        workedDuration: "0h 0min 0sec"
      });
      return;
    }

    let totalDurationSeconds = 0;
    let totalworkedDuration = 0
    const result = data.map((item) => {
      const startDate = new Date(item.startTime);
      const endDate = new Date(item.endTime);
      const start = startDate.getHours() + startDate.getMinutes() / 60 + startDate.getSeconds() / 3600;

      const durationSeconds = (endDate - startDate) / 1000;
      totalDurationSeconds += durationSeconds;
      if (item.billable) {
        totalworkedDuration += durationSeconds;
      }
      const durationHours = durationSeconds / 3600;

      const ownerName = item.owner?.name || "Unknown";

      return {
        start: parseFloat(start.toFixed(2)),
        duration: parseFloat(durationHours.toFixed(3)),
        activity: item.title,
        ownerName,               // Store ownerName
        rawStart: startDate,
        rawEnd: endDate,
      };
    });

    setActivityDetails(result);

    const earliestStart = new Date(Math.min(...result.map(a => a.rawStart.getTime())));
    const latestEnd = new Date(Math.max(...result.map(a => a.rawEnd.getTime())));

    const hours = Math.floor(totalDurationSeconds / 3600);
    const minutes = Math.floor((totalDurationSeconds % 3600) / 60);
    const seconds = Math.floor(totalDurationSeconds % 60);
    const whours = Math.floor(totalworkedDuration / 3600);
    const wminutes = Math.floor((totalworkedDuration % 3600) / 60);
    const wseconds = Math.floor(totalworkedDuration % 60);

    setTimeData({
      startTime: moment(earliestStart).format("HH:mm"),
      endTime: moment(latestEnd).format("HH:mm"),
      duration: `${hours}h ${minutes}min ${seconds}sec`,
      workedDuration: `${whours}h ${wminutes}min ${wseconds}sec`
    });
  };

  const handleDateNav = (delta) => {
    const updated = moment(currentDate).add(delta, "days");
    setCurrentDate(updated);
  };

  const paginateData = (data, page, limit) => {
    const start = (page - 1) * limit;
    const end = start + limit;
    return data.slice(start, end);
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const sortData = (data, key, direction) => {
    if (key === "time") key = "startTime";
    if (key === "processName") key = "owner.name";

    return [...data].sort((a, b) => {
      const valA = getNestedValue(a, key);
      const valB = getNestedValue(b, key);

      if (valA == null && valB == null) return 0;
      if (valA == null) return direction === 'asc' ? -1 : 1;
      if (valB == null) return direction === 'asc' ? 1 : -1;

      if (typeof valA === 'string') {
        return direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };




  const getDayWiseData = async () => {
    try {
      const formattedDate = currentDate.format("YYYY-MM-DD");
      const result = await getDayWiseTracking(login.token, { date: formattedDate, userId: selectedUser });
      const allData = result.data || [];

      setFullData(allData);

      const sorted = sortData(allData, filter.key, filter.direction);
      const paginated = paginateData(sorted, filter.page, filter.limit);
      setSyncedWindows(paginated);

      convertToActivityData(allData);
    } catch (error) {
      console.error(error);
      setActivityDetails([]);
      setTimeData({
        startTime: "00:00",
        endTime: "00:00",
        duration: "0h 0min 0sec",
        workedDuration: "0h 0min 0sec"
      });
    }
  };

  const datePickerRef = useRef(null);

  const openCalendar = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  useEffect(() => {
    getAllUsers()
  }, [])

  useEffect(() => {
    document.title = "Day Tracker | Prabhu Chat";
    getDayWiseData();
  }, [currentDate, selectedUser]);

  useEffect(() => {
    const sorted = sortData(fullData, filter.key, filter.direction);
    const paginated = paginateData(sorted, filter.page, filter.limit);
    setSyncedWindows(paginated);
  }, [filter, fullData]);


  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const currentTimeLineLeft = `${(currentHour / totalHours) * 100}%`;

  const totalDurationsByOwner = activityDetails.reduce((acc, act) => {
    acc[act.ownerName] = (acc[act.ownerName] || 0) + act.duration;
    return acc;
  }, {});

  return (
    <div className="container-fluid py-2">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="time-tracker-card">
            {/* Header */}
            <div className="row align-items-center g-4 justify-content-between" style={{ marginInlineStart: "5px" }}>
              <div className="d-flex">
                <div className="row gap-3 w-100">
                  <div className="col-md-2 stat-card started-card" style={{ flexDirection: "column" }}>
                    <div className="stat-value">{timeData.startTime}</div>
                    <div className="stat-label"><i className="fas fa-play-circle me-1" />Started</div>
                  </div>
                  <div className="col-md-2 stat-card finished-card" style={{ flexDirection: "column" }}>
                    <div className="stat-value">{timeData.endTime}</div>
                    <div className="stat-label"><i className="fas fa-stop-circle me-1" />Finished</div>
                  </div>
                  <div className="col-md-2 stat-card duration-card" style={{ flexDirection: "column" }}>
                    <div className="stat-value">{timeData.duration}</div>
                    <div className="stat-label"><i className="fas fa-clock me-1" />Total Duration</div>
                  </div>
                  <div className="col-md-2 stat-card duration-card" style={{ flexDirection: "column" }}>
                    <div className="stat-value">{timeData.workedDuration}</div>
                    <div className="stat-label"><i className="fas fa-clock me-1" />Duration</div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="row align-items-center g-2 justify-content-end">
                    <div className="col-2 d-flex justify-content-center">
                      <div className="nav-arrow" onClick={() => handleDateNav(-1)}><i className="fas fa-chevron-left" /></div>
                    </div>
                    <div className="col-7" style={{ zIndex: "1", width: "fit-content" }} onClick={openCalendar}>
                      <div className="date-picker-section d-flex align-items-center">
                        <i className="fas fa-calendar-alt calendar-icon me-2"></i>
                        <DatePicker
                          ref={datePickerRef}
                          selected={currentDate.toDate()}
                          onChange={(date) => {
                            const newDate = moment(date);
                            setCurrentDate(newDate);
                          }}
                          dateFormat="EEE, MMM dd"
                          className="form-control"
                          popperPlacement="bottom"
                          calendarClassName="custom-datepicker"
                          style={{ border: "none" }}
                        />
                      </div>
                    </div>
                    <div className="col-2 d-flex justify-content-center">
                      <div className="nav-arrow" onClick={() => handleDateNav(1)}><i className="fas fa-chevron-right" /></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <SearchableDropdown
                  label="Name "
                  options={users} // expects array of objects [{ _id, name }]
                  selected={selectedUser}
                  onChange={(val) => setSelectedUser(val)}
                  placeholder="Select User"
                />
              </div>
            </div>

            {/* Timeline */}
            <div className="timeline-container">
              <div className="timeline-scroll-wrapper" id="timelineScroller">
                <div className="time-labels">
                  {[...Array(24).keys()].map(hour => (
                    <div
                      key={hour}
                      className="time-label"
                      style={{ left: `${(hour / totalHours) * 100}%` }}
                    >
                      {hour.toString().padStart(2, "0")}:00
                    </div>
                  ))}
                </div>
                <div className="timeline-wrapper">
                  <div className="hour-markers" id="hourMarkers">
                    {[...Array(24).keys()].map(hour => (
                      <div
                        key={hour}
                        className={`${hour % 6 === 0 ? "major-hour-marker" : "hour-marker"}`}
                        style={{ left: `${(hour / totalHours) * 100}%` }}
                      />
                    ))}
                  </div>
                  <div className="current-time-line" style={{ left: currentTimeLineLeft }} />
                  <div className="timeline-content">
                    {activityDetails.map((activity, i) => (
                      <div
                        key={i}
                        className="activity-segment"
                        style={{
                          left: `${(activity.start / totalHours) * 100}%`,
                          width: `${(activity.duration / totalHours) * 100}%`,
                          backgroundColor: ownerColorMap[activity.ownerName] || "#999"
                        }}
                        onMouseEnter={(e) => {
                          const startTime = formatTime(activity.start);
                          const endTime = formatTime(activity.start + activity.duration);
                          const duration = formatAppWiseDuration(activity.duration);
                          setTooltip({
                            visible: true,
                            content: `<strong>${activity.activity}</strong><br />
                                      ${startTime} - ${endTime}<br />
                                      Duration: ${duration}`,
                            x: e.pageX,
                            y: e.pageY,
                          });
                        }}
                        onMouseMove={(e) => {
                          setTooltip(prev => ({
                            ...prev,
                            x: e.pageX,
                            y: e.pageY,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip({ visible: false, content: '', x: 0, y: 0 });
                        }}
                      >
                        {/* <div className="hover-tooltip">
                          <strong>{activity.activity}</strong>
                          <p>{formatTime(activity.start)} - {formatTime(activity.start + activity.duration)}</p>
                          <p>Duration: {formatDuration(activity.duration)}</p>
                        </div> */}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline Stats */}
                <div className="timeline-stats">
                  {Object.entries(totalDurationsByOwner).map(([ownerName, duration]) => (
                    <div key={ownerName} className="stat-item">
                      <div
                        className="stat-color"
                        style={{ backgroundColor: ownerColorMap[ownerName] || "#ccc" }}
                      />
                      <span title={ownerName}>
                        {`${ownerName.length > 25 ? ownerName.slice(0, 25) + "..." : ownerName}: ${formatAppWiseDuration(duration)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Tooltip */}
            {tooltip.visible && (
              <div
                className="tooltip show"
                style={{ left: tooltip.x + 10, top: tooltip.y + 10 }}
                dangerouslySetInnerHTML={{ __html: tooltip.content }}
              />
            )}

          </div>
        </div>
      </div>
      {/* <div className="d-flex justify-content-between" style={{ "padding-block-start": "15px" }}>
        <div className="d-flex gap-3 align-items-center">
          {login?.user?.settings?.manualEntry && <button
            onClick={() => setIsModalOpen(true)}
            className="btn"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            Add Manual Entry
          </button>}
        </div>
      </div> */}
      {/* {<div className="accordion my-4" id="customAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header" id="headingOne">
            <button
              className="accordion-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseOne"
              aria-expanded="false"
              aria-controls="collapseOne"
            >
              Detailed View
            </button>
          </h2>
          <div
            id="collapseOne"
            className="accordion-collapse collapse"
            aria-labelledby="headingOne"
            data-bs-parent="#customAccordion"
          >
            <div className="accordion-body">
              <div>
                {!loading ? <>
                  {error && <p>
                    {error}
                  </p>}
                  <div className="custom-pagination">
                    <div className="d-flex gap-2 align-items-center">
                      <span className="showing-entries">Showing {(filter.page - 1) * filter.limit + 1} - {Math.min(filter.page * filter.limit, fullData.length)} of {fullData.length}</span>
                      <div className="dropdown">
                        <select className="form-select" value={filter.limit} onChange={handleLimitChange}>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="150">150</option>
                          <option value="200">200</option>
                        </select>
                      </div>
                      {selectedIds.length > 0 && (
                        <div>
                          <button className="btn btn-danger" onClick={() => setConformationModal(true)}>Delete</button>
                        </div>
                      )}
                    </div>
                    <div className="page-controls">
                      <button
                        className="page-btn prev"
                        onClick={handlePrev}
                        disabled={pagination.currentPage === 1} // Disable button on first page
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                      <span className="current-page">{filter.page}</span>
                      <span className="separator">/</span>
                      <span className="total-pages">{totalPages}</span>
                      <button
                        className="page-btn next"
                        onClick={handleNext}
                        disabled={filter.page === totalPages} // Disable button on last page
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                  <div>
                    <WindowTable windows={syncedWindows} formatDuration={formatDuration} handleSort={handleSort} filter={filter} selectedIds={selectedIds} setSelectedIds={setSelectedIds} handleCheckboxChange={handleCheckboxChange} settings={login?.user?.settings || {}} />
                  </div>
                </> : <>
                  <div className="heartbeat-loader">
                    <img
                      src={CONFIG.REACT_APP_UPLOADS + "/Icons/logo.png"}
                      alt="Heartbeat Loader"
                      className="heartbeat-image"
                      style={{ width: "100px" }}
                    />
                  </div>
                </>}
                <div className={`modal fade ${conformationModal ? "show d-block" : ""}`} tabIndex="-1" role="dialog">
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Confirm Action</h5>
                        <button type="button" className="btn-close" onClick={() => setConformationModal(false)}></button>
                      </div>
                      <div className="modal-body">Are you sure you want to delete this item?</div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setConformationModal(false)}>Cancel</button>
                        <button type="button" className="btn btn-danger" onClick={() => { deleteTime() }}>Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
                {conformationModal && <div className="modal-backdrop fade show" onClick={() => setConformationModal(false)}></div>}
                <ManualEntryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} />
              </div>
            </div>
          </div>
        </div>
      </div>} */}



    </div >
  );
};

const WindowTable = ({ windows, formatDuration, handleSort, filter, selectedIds, setSelectedIds, handleCheckboxChange, settings }) => (
  <table className="table table-bordered">
    <thead className="table-dark">
      <tr>
        {settings.deleteEntry && <th style={{ cursor: "pointer" }} onClick={() => { selectedIds.length == windows.length ? setSelectedIds([]) : setSelectedIds(windows.map(x => { return x._id })) }}>All</th>}
        <th style={{ width: "10px" }}>No</th>
        <th
          style={{ wordBreak: "break-word", cursor: "pointer" }}
          onClick={() => handleSort('title')}
        >
          Title
          {filter.key === 'title' && (filter.direction === 'asc' ? ' ▲' : ' ▼')}
        </th>
        <th
          style={{ cursor: "pointer", width: "200px" }}
          onClick={() => handleSort('processName')}
        >
          Process Name
          {filter.key === 'processName' && (filter.direction === 'asc' ? ' ▲' : ' ▼')}
        </th>
        <th
          className="justify-content-center"
          style={{ cursor: "pointer", width: "150px" }}
          onClick={() => handleSort('time')}
        >
          Time
          {filter.key === 'time' && (filter.direction === 'asc' ? ' ▲' : ' ▼')}
        </th>
        <th
          style={{ cursor: "pointer", width: "100px" }}
          onClick={() => handleSort('duration')}
        >
          Duration
          {filter.key === 'duration' && (filter.direction === 'asc' ? ' ▲' : ' ▼')}
        </th>
      </tr>
    </thead>
    <tbody>
      {
        windows.length == 0 && <>No Records Found</>
      }
      {windows.map((window, key) => {
        // Format start time
        const startTimeFormatted = window.startTime
          ? {
            time: moment(window.startTime).format("HH:mm"), // Time
            date: moment(window.startTime).format("DD-MM-YY"), // Date
          }
          : { time: "N/A", date: "N/A" };

        // Format end time
        const endTimeFormatted = window.endTime
          ? {
            time: moment(window.endTime).format("HH:mm"), // Time
            date: moment(window.endTime).format("DD-MM-YY"), // Date
          }
          : { time: "Active", date: "" };

        // Calculate duration
        const duration = window.endTime
          ? moment(window.endTime).diff(moment(window.startTime), "seconds")
          : moment().diff(moment(window.startTime), "seconds");

        return (
          <tr key={key}>
            {settings.deleteEntry && <td><div className="form-check d-flex justify-content-center" style={{ fontSize: "20px" }}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`checkbox-${window._id}`}
                checked={selectedIds.includes(window._id)}
                onChange={(e) => handleCheckboxChange(window._id, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`checkbox-${window._id}`}></label>
            </div></td>}
            <td>{(filter.page - 1) * filter.limit + key + 1}</td>
            <td style={{ wordBreak: "break-word" }}>{window?.title}</td>
            <td>{window?.owner?.name}</td>
            <td className="justify-content-center">
              <div className="time-style">
                <div>{startTimeFormatted.time}&nbsp; &nbsp;{endTimeFormatted.time}</div>
                <div style={{ "placeSelf": "center" }}>{startTimeFormatted.date}</div>
              </div>
            </td>
            {/* <td>
              
              <div>{endTimeFormatted.date}</div>
            </td> */}
            <td>
              {duration > 0 ? formatDuration(parseInt(duration)) : duration}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);